import React, { useState, useEffect, useCallback } from 'react';
import ChatHeader from './ChatHeader';
import MessageList from './MessageList';
import ChatInput from './ChatInput';
import ChatFAB from './ChatFAB';
import { useSession } from '../../hooks/useSession';
import { useChat } from '../../hooks/useChat';

export interface ChatOpenRequest {
  query: string;
  // Bumped per request so the same query can be sent twice.
  nonce: number;
}

interface ChatWidgetProps {
  openRequest: ChatOpenRequest | null;
}

// Opening the chat no longer waits on the backend. It used to: the widget held
// a 'waking' spinner until a wake-up handshake reported the Flask app on
// Render had finished its 30-60s cold start. The backend is a Cloudflare
// Worker now, which has no cold start, so there was nothing left to wait for —
// but the wait didn't go away, because the widget learned about readiness by
// polling the wake-up service on a 1s interval (and 500ms inside
// ensureReady()). That put a floor of roughly a poll tick on the spinner no
// matter how fast the backend answered.
//
// Nothing here needs the backend anyway: the panel renders from local state,
// and useChat already surfaces every failure inline as a TARS-voiced system
// message (including "Can't reach the server"), so a backend outage is
// reported when it actually matters — on send.
const ChatWidget: React.FC<ChatWidgetProps> = ({ openRequest }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  // Desktop-only: below lg the panel already fills the viewport.
  const [isExpanded, setIsExpanded] = useState(false);
  const { sessionId } = useSession();
  const { messages, isTyping, streamingMessageId, sendMessage, clearMessages } = useChat(sessionId);

  // Hides the widget but keeps the conversation — the only way to wipe
  // history is the explicit "clear chat" action, which confirms first.
  const handleClose = useCallback(() => {
    setIsMinimized(true);
    setTimeout(() => {
      setIsOpen(false);
      setIsMinimized(false);
    }, 300);
  }, []);

  const handleToggleExpand = useCallback(() => {
    setIsExpanded((expanded) => !expanded);
  }, []);

  // Escape steps back one level rather than jumping straight out: from
  // expanded it collapses, and only a docked panel closes. Closing an expanded
  // panel outright is a bigger, more surprising jump than the key implies.
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || !isOpen) {
        return;
      }
      if (isExpanded) {
        setIsExpanded(false);
      } else {
        handleClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, isExpanded, handleClose]);

  const handleOpen = useCallback(() => {
    setIsOpen(true);
    setIsMinimized(false);
  }, []);

  // Command palette handed us a question: open the widget and send it. The
  // send is unconditional now — it used to be gated on the wake-up service
  // reporting ready, which silently dropped the question if that handshake
  // failed. A failure now comes back as an error message in the thread.
  useEffect(() => {
    if (!openRequest) {
      return;
    }
    handleOpen();
    sendMessage(openRequest.query);
    // Both callbacks are stable enough to depend on honestly: handleOpen has
    // no deps, and sendMessage only changes when the session ID does, so
    // neither re-fires this for an unchanged nonce.
  }, [openRequest, handleOpen, sendMessage]);

  const handleClear = () => {
    if (confirm('Are you sure you want to clear the chat history?')) {
      clearMessages();
    }
  };

  const handleSuggestedClick = (question: string) => {
    sendMessage(question);
  };

  if (!isOpen) {
    return <ChatFAB onClick={handleOpen} />;
  }

  return (
    <>
      {/* Backdrop for mobile, and for the expanded panel on desktop — once the
          card covers most of the screen, the page behind it is noise. */}
      <div
        className={`fixed inset-0 bg-black/50 backdrop-blur-sm z-40 transition-opacity duration-300 ${
          isExpanded ? '' : 'lg:hidden'
        } ${isMinimized ? 'opacity-0' : 'opacity-100'}`}
        onClick={isExpanded ? handleToggleExpand : handleClose}
        data-lens-exclude
      />

      {/* Chat Window */}
      <div
        className={`fixed z-50 flex flex-col bg-dev-bg border border-dev-border shadow-dev-lg rounded-xl overflow-hidden transition-all duration-300 ${
          isMinimized
            ? 'bottom-6 right-6 w-14 h-14 opacity-0 scale-50'
            : `bottom-0 right-0 w-full h-dvh max-h-dvh lg:bottom-6 lg:right-6 lg:rounded-xl opacity-100 scale-100 ${
                isExpanded
                  ? 'lg:w-[min(800px,calc(100vw_-_3rem))] lg:h-[min(900px,calc(100vh_-_3rem))] lg:max-h-[calc(100vh_-_3rem)]'
                  : 'lg:w-[420px] lg:h-[650px] lg:max-h-[650px]'
              }`
        }`}
        data-lens-exclude
      >
        <ChatHeader
          onClose={handleClose}
          onClear={handleClear}
          onToggleExpand={handleToggleExpand}
          isExpanded={isExpanded}
          isTyping={isTyping || !!streamingMessageId}
        />

        <MessageList
          messages={messages}
          isTyping={isTyping}
          streamingMessageId={streamingMessageId}
          isExpanded={isExpanded}
          onSuggestedClick={handleSuggestedClick}
        />
        <ChatInput onSend={sendMessage} disabled={isTyping || !!streamingMessageId} />
      </div>
    </>
  );
};

export default ChatWidget;
